import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../lib/firebase";

const SEO_COLLECTION = "seo_settings";

export class SEOService {
  // Get all SEO settings
  static async getAllSEOSettings() {
    try {
      const querySnapshot = await getDocs(collection(db, SEO_COLLECTION));
      const seoSettings = [];
      querySnapshot.forEach((doc) => {
        seoSettings.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      return seoSettings;
    } catch (error) {
      console.error("Error fetching SEO settings:", error);
      throw error;
    }
  }

  // Get SEO settings by page path
  static async getSEOSettingsByPath(pagePath) {
    try {
      const q = query(
        collection(db, SEO_COLLECTION),
        where("pagePath", "==", pagePath)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching SEO settings by path:", error);
      throw error;
    }
  }

  // Get SEO settings by ID
  static async getSEOSettingsById(id) {
    try {
      const docRef = doc(db, SEO_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching SEO settings by ID:", error);
      throw error;
    }
  }

  // Create new SEO settings
  static async createSEOSettings(seoData) {
    try {
      const seoSettings = {
        ...seoData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
      };

      const docRef = await addDoc(collection(db, SEO_COLLECTION), seoSettings);
      return {
        id: docRef.id,
        ...seoSettings,
      };
    } catch (error) {
      console.error("Error creating SEO settings:", error);
      throw error;
    }
  }

  // Update SEO settings
  static async updateSEOSettings(id, seoData) {
    try {
      const docRef = doc(db, SEO_COLLECTION, id);
      const updateData = {
        ...seoData,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      return {
        id,
        ...updateData,
      };
    } catch (error) {
      console.error("Error updating SEO settings:", error);
      throw error;
    }
  }

  // Delete SEO settings
  static async deleteSEOSettings(id) {
    try {
      const docRef = doc(db, SEO_COLLECTION, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error("Error deleting SEO settings:", error);
      throw error;
    }
  }

  // Get active SEO settings for a page
  static async getActiveSEOSettings(pagePath) {
    try {
      const q = query(
        collection(db, SEO_COLLECTION),
        where("pagePath", "==", pagePath),
        where("isActive", "==", true)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching active SEO settings:", error);
      throw error;
    }
  }

  // Get all page paths
  static async getAllPagePaths() {
    try {
      const querySnapshot = await getDocs(collection(db, SEO_COLLECTION));
      const pagePaths = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.pagePath) {
          pagePaths.push(data.pagePath);
        }
      });
      return [...new Set(pagePaths)]; // Remove duplicates
    } catch (error) {
      console.error("Error fetching page paths:", error);
      throw error;
    }
  }

  // Bulk update SEO settings
  static async bulkUpdateSEOSettings(updates) {
    try {
      const updatePromises = updates.map(({ id, data }) =>
        this.updateSEOSettings(id, data)
      );
      return await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error bulk updating SEO settings:", error);
      throw error;
    }
  }

  // Search SEO settings
  static async searchSEOSettings(searchTerm) {
    try {
      const querySnapshot = await getDocs(collection(db, SEO_COLLECTION));
      const results = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const searchableText =
          `${data.title} ${data.description} ${data.keywords} ${data.pagePath}`.toLowerCase();

        if (searchableText.includes(searchTerm.toLowerCase())) {
          results.push({
            id: doc.id,
            ...data,
          });
        }
      });

      return results;
    } catch (error) {
      console.error("Error searching SEO settings:", error);
      throw error;
    }
  }

  // Preload all SEO settings for static generation
  static async preloadAllSEOSettings() {
    try {
      const settings = await this.getAllSEOSettings();
      // Store in localStorage for static generation access
      localStorage.setItem("seo_settings_cache", JSON.stringify(settings));
      return settings;
    } catch (error) {
      console.error("Error preloading SEO settings:", error);
      return [];
    }
  }

  // Get SEO settings from cache (for static generation)
  static getSEOSettingsFromCache(pagePath) {
    try {
      const cached = localStorage.getItem("seo_settings_cache");
      if (cached) {
        const settings = JSON.parse(cached);
        return (
          settings.find((s) => s.pagePath === pagePath && s.isActive) || null
        );
      }
      return null;
    } catch (error) {
      console.error("Error getting SEO settings from cache:", error);
      return null;
    }
  }

  // Initialize SEO cache
  static async initializeSEOCache() {
    if (typeof window !== "undefined") {
      await this.preloadAllSEOSettings();
    }
  }
}
